import { useState } from "react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const clinics = [
  {
    id: 1,
    city: "Ljubljana",
    address: "Rožna ulica 7, 1000 Ljubljana",
    phone: "+386 41 123 456",
    email: "ljubljana@danijelnovak.si",
    hours: "Pon-Pet: 9:00-17:00",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2769.220184274315!2d14.504573784699537!3d46.05127057338145!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47652d6234243355%3A0xf7008bbffb0946f!2zUm_FvmEgdWxpY2EgNywgMTAwMCBManVibGphbmEsIFNsb3ZlbmlqYQ!5e0!3m2!1ssl!2sus!4v1652341169382!5m2!1ssl!2sus"
  },
  {
    id: 2,
    city: "Celje",
    address: "Bežigrajska cesta 12, 3000 Celje",
    phone: "+386 41 123 457",
    email: "celje@danijelnovak.si",
    hours: "Pon, Sre, Pet: 9:00-17:00",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2759.3024251355597!2d15.262801784709005!3d46.23580047338825!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47657feabedb9a0f%3A0x638123599a0eff0a!2zQmXFvmlncmFqc2thIGNlc3RhIDEyLCAzMDAwIENlbGplLCBTbG92ZW5pamE!5e0!3m2!1ssl!2sus!4v1652341074938!5m2!1ssl!2sus"
  }
];

const ContactPage = () => {
  const { toast } = useToast();
  const [activeLocation, setActiveLocation] = useState(clinics[0].id);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    location: "Ljubljana"
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, location: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Sporočilo poslano",
        description: "Hvala za vaše sporočilo. Odgovorili vam bomo v najkrajšem možnem času.",
      });
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        location: "Ljubljana"
      });
      
      setIsSubmitting(false);
    }, 1000);
  };

  const selectedClinic = clinics.find(clinic => clinic.id === activeLocation) || clinics[0];

  return (
    <Layout>
      <PageHeader
        title="Kontakt"
        description="Stopite v stik z nami za več informacij ali za rezervacijo posveta."
      />
      
      {/* Contact Form and Info Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Naše lokacije</h2>
              
              <div className="flex gap-3 mb-8">
                {clinics.map(clinic => (
                  <Button 
                    key={clinic.id}
                    variant={activeLocation === clinic.id ? "default" : "outline"}
                    className={`rounded-full ${activeLocation === clinic.id ? 'bg-medical-500' : 'border-medical-300'}`}
                    onClick={() => setActiveLocation(clinic.id)}
                  >
                    {clinic.city}
                  </Button>
                ))}
              </div>
              
              <div className="glass-card p-6 md:p-8 mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Klinika {selectedClinic.city}
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-medical-100 p-2 rounded-full text-medical-600 mr-4">
                      <MapPin size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Naslov</p>
                      <p className="text-gray-700">{selectedClinic.address}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-medical-100 p-2 rounded-full text-medical-600 mr-4">
                      <Phone size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Telefon</p>
                      <a href={`tel:${selectedClinic.phone}`} className="text-gray-700 hover:text-medical-600">
                        {selectedClinic.phone}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-medical-100 p-2 rounded-full text-medical-600 mr-4">
                      <Mail size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Email</p>
                      <a href={`mailto:${selectedClinic.email}`} className="text-gray-700 hover:text-medical-600">
                        {selectedClinic.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-medical-100 p-2 rounded-full text-medical-600 mr-4">
                      <Clock size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Delovni čas</p>
                      <p className="text-gray-700">{selectedClinic.hours}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Map */}
              <div className="rounded-3xl overflow-hidden shadow-sm h-64">
                <iframe 
                  src={selectedClinic.mapUrl} 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Klinika ${selectedClinic.city} map`}
                ></iframe>
              </div>
            </div>
            
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Pišite nam</h2>
              <div className="glass-card p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Ime in priimek</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Janez Novak"
                      required
                      className="rounded-lg border-gray-300 focus:border-medical-500 focus:ring-medical-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="janez.novak@example.com"
                        required
                        className="rounded-lg border-gray-300 focus:border-medical-500 focus:ring-medical-500"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+386 41 123 456"
                        className="rounded-lg border-gray-300 focus:border-medical-500 focus:ring-medical-500"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="location">Izbrana lokacija</Label>
                    <Select
                      value={formData.location}
                      onValueChange={handleSelectChange}
                    >
                      <SelectTrigger className="rounded-lg border-gray-300 focus:border-medical-500 focus:ring-medical-500">
                        <SelectValue placeholder="Izberite lokacijo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ljubljana">Ljubljana</SelectItem>
                        <SelectItem value="Celje">Celje</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subject">Zadeva</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Posvet za..."
                      required
                      className="rounded-lg border-gray-300 focus:border-medical-500 focus:ring-medical-500"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Sporočilo</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Vnesite svoje sporočilo..."
                      rows={5}
                      required
                      className="rounded-lg border-gray-300 focus:border-medical-500 focus:ring-medical-500"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="glass-card bg-medical-500 hover:bg-medical-600 text-white rounded-full btn-hover-effect w-full"
                  >
                    {isSubmitting ? "Pošiljanje..." : "Pošlji sporočilo"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;